import { APP_CONFIG } from '@config/constants'
import ApiService from './api'

function buildQuery(filters = {}, extras = {}) {
  const params = new URLSearchParams()
  Object.entries({ ...filters, ...extras }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  })
  return params.toString()
}

async function requestMultipart(endpoint, formData) {
  try {
    const response = await fetch(`${APP_CONFIG.API_BASE}${endpoint}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
    const contentType = response.headers.get('content-type') || ''
    const payload = contentType.includes('application/json') ? await response.json() : null
    if (response.status === 401) window.dispatchEvent(new Event('auth:session-expired'))
    if (!response.ok) throw new Error(payload?.detail || payload?.message || `HTTP ${response.status}`)
    return { success: true, data: payload }
  } catch (error) {
    return { success: false, error: error.message || 'Não foi possível enviar o arquivo.' }
  }
}

function documentFormData(file, { processoId = null, requestId = null, descricao = '', clienteId = null } = {}) {
  const form = new FormData()
  form.append('file', file)
  if (processoId) form.append('processoId', String(processoId))
  if (requestId) form.append('requestId', String(requestId))
  if (clienteId) form.append('clienteId', String(clienteId))
  if (descricao) form.append('descricao', descricao)
  return form
}

export function requestPortalAccess(payload) {
  return ApiService.request('portal/access-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function getPortalContext() {
  return ApiService.request('portal/me')
}

export function getPortalDashboard(filters = {}) {
  const query = buildQuery(filters)
  return ApiService.request(`portal/dashboard${query ? `?${query}` : ''}`)
}

export function getPortalProcesses(filters = {}, { page = 1, pageSize = 25, search = filters.busca || '' } = {}) {
  const query = buildQuery(filters, { pagina: page, tamanhoPagina: pageSize, busca: search.trim() })
  return ApiService.request(`portal/processes?${query}`)
}

export function getPortalProcessDetail(processoId) {
  return ApiService.request(`portal/processes/${processoId}`)
}

export function getPortalFilterOptions(estado = '') {
  const query = buildQuery({ estado })
  return ApiService.request(`portal/filter-options${query ? `?${query}` : ''}`)
}

export function getPortalHearings(filters = {}, { page = 1, pageSize = 25 } = {}) {
  const query = buildQuery(filters, { pagina: page, tamanhoPagina: pageSize })
  return ApiService.request(`portal/audiencias?${query}`)
}

export function exportPortalReport(filters = {}) {
  const query = buildQuery(filters)
  return ApiService.requestBlob(`portal/export/xlsx${query ? `?${query}` : ''}`, { timeoutMs: 600000 })
}

export function getPortalDocuments({ processoId = null, requestId = null } = {}) {
  const query = buildQuery({}, { processoId, requestId })
  return ApiService.request(`portal/documents${query ? `?${query}` : ''}`)
}

export function uploadPortalDocument(file, metadata = {}) {
  return requestMultipart('portal/documents', documentFormData(file, metadata))
}

export function downloadPortalDocument(documentId) {
  return ApiService.requestBlob(`portal/documents/${documentId}/download`, { timeoutMs: 120000 })
}

export function getPortalClientRequests(status = '') {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return ApiService.request(`portal/client-requests${query}`)
}

export function answerPortalClientRequest(requestId, resposta = '') {
  return ApiService.request(`portal/client-requests/${requestId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ resposta: resposta || null }),
  })
}

export function getPortalAccessRequests(status = '') {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return ApiService.request(`portal/admin/access-requests${query}`)
}

export function approvePortalAccessRequest(requestId, clienteId, observacao = '') {
  return ApiService.request(`portal/admin/access-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ clienteId: Number(clienteId), observacao: observacao || null }),
  })
}

export function rejectPortalAccessRequest(requestId, observacao = '') {
  return ApiService.request(`portal/admin/access-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ observacao: observacao || null }),
  })
}

export function generatePortalTemporaryPasswordForRequest(requestId) {
  return ApiService.request(`portal/admin/access-requests/${requestId}/temporary-password`, {
    method: 'POST',
  })
}

export function resendPortalAccessLink(requestId) {
  return ApiService.request(`portal/admin/access-requests/${requestId}/resend-access-link`, {
    method: 'POST',
  })
}

export function changePortalAccessRequestClient(requestId, clienteId) {
  return ApiService.request(`portal/admin/access-requests/${requestId}/change-client`, {
    method: 'POST',
    body: JSON.stringify({ clienteId: Number(clienteId) }),
  })
}

export function removePortalAccessRequestUser(requestId) {
  return ApiService.request(`portal/admin/access-requests/${requestId}/user`, {
    method: 'DELETE',
  })
}

export function generatePortalTemporaryPasswordForUser(userId) {
  return ApiService.request(`portal/admin/users/${userId}/temporary-password`, {
    method: 'POST',
  })
}

export function getAdminPortalClientRequests({ clienteId = null, status = '' } = {}) {
  const query = buildQuery({}, { clienteId, status })
  return ApiService.request(`portal/admin/client-requests${query ? `?${query}` : ''}`)
}

export function createAdminPortalClientRequest(payload) {
  return ApiService.request('portal/admin/client-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function closeAdminPortalClientRequest(requestId) {
  return ApiService.request(`portal/admin/client-requests/${requestId}/close`, { method: 'POST' })
}

export function getAdminPortalDocuments({ clienteId, processoId = null }) {
  const query = buildQuery({}, { clienteId, processoId })
  return ApiService.request(`portal/admin/documents?${query}`)
}

export function uploadAdminPortalDocument(file, metadata) {
  return requestMultipart('portal/admin/documents', documentFormData(file, metadata))
}

export function downloadAdminPortalDocument(documentId) {
  return ApiService.requestBlob(`portal/admin/documents/${documentId}/download`, { timeoutMs: 120000 })
}
